/**
 * payment-error — error-envelope → taxonomy mapper (Phase 5.3, BILL-10 / D-07).
 *
 * The API returns payment failures in TWO envelope shapes (RESEARCH §5.6):
 *   - `{ message }`                — overpay / duplicate (409, ConflictError text)
 *   - `{ status, error, message }` — decline / gateway (ActiveMerchant::ResponseError,
 *                                     401 remapped to 409, `message=friendly_message`)
 * `.message` is present in BOTH, so we key off it. SDK-load failures (the
 * `<script>.onerror` path from `useGateway`) carry no envelope at all.
 *
 * Taxonomy (Pitfall 6 / Appendix B#9, C) — the difference between a Sentry
 * incident and a silent business outcome:
 *
 *   | kind          | meaning                                  | alertable |
 *   | ------------- | ---------------------------------------- | --------- |
 *   | validation    | inline, keep state (overpay/duplicate)   | false     |
 *   | declined      | hard bank decline, retryable             | false     |
 *   | soft-decline  | issuer asks for retry/auth (e.g. 3DS)    | false     |
 *   | gateway       | gateway/processor rejected (AVS/CVV/etc) | true      |
 *   | system        | SDK load / network / unknown failure     | true      |
 *
 * CRITICAL (Pitfall 6 / T-05.3-10): an AVS or CVV rejection is a GATEWAY outcome,
 * NEVER labelled a bank `declined` — over-claiming a bank decline misreports why
 * the payment failed. A decline is a business outcome (breadcrumb + Pendo only);
 * a gateway/system failure is alertable in Sentry.
 */

export type PaymentErrorKind =
  | 'validation'
  | 'declined'
  | 'soft-decline'
  | 'gateway'
  | 'system';

export interface PaymentErrorClassification {
  kind: PaymentErrorKind;
  /** Customer-facing message extracted from the envelope (never leaks internals). */
  message: string;
  /** True only for gateway/system failures — declines stay quiet (no Sentry alert). */
  alertable: boolean;
}

/** Loose shape covering an axios error or a raw thrown value. */
interface ErrorEnvelopeLike {
  message?: unknown;
  status?: unknown;
  error?: unknown;
  code?: unknown;
  response?: {
    status?: unknown;
    data?: {
      message?: unknown;
      status?: unknown;
      error?: unknown;
      code?: unknown;
    };
  };
}

const GENERIC_MESSAGE = 'Something went wrong processing your payment.';

/** Extract `.message` from either bare envelope or an axios `response.data`. */
function extractMessage(err: ErrorEnvelopeLike): string {
  const data = err.response?.data;
  const candidate =
    (typeof data?.message === 'string' && data.message) ||
    (typeof err.message === 'string' && err.message) ||
    '';
  return candidate || GENERIC_MESSAGE;
}

/** Pull an HTTP status from the envelope or axios `response.status`. */
function extractStatus(err: ErrorEnvelopeLike): number | undefined {
  const raw = err.response?.status ?? err.status ?? err.response?.data?.status;
  return typeof raw === 'number' ? raw : undefined;
}

/** Pull a gateway/processor code if present. */
function extractCode(err: ErrorEnvelopeLike): string {
  const raw = err.response?.data?.code ?? err.code;
  return typeof raw === 'string' ? raw.toLowerCase() : '';
}

/**
 * Ordered text-rule table. Resolution order matters: validation
 * (user-correctable) → AVS/CVV gateway (must NOT be called a decline, Pitfall 6)
 * → soft-decline → hard decline → explicit system text. First match wins. A flat
 * table (rather than a chain of `if`s) keeps the classifier under the
 * cyclomatic-complexity ceiling, mirroring the `invoice-status.ts` convention.
 */
const MESSAGE_RULES: ReadonlyArray<{
  re: RegExp;
  kind: PaymentErrorKind;
  alertable: boolean;
}> = [
  // Validation: overpay / duplicate / balance — the user can correct the amount.
  {
    re: /\b(more than the balance|exceeds? the balance|balance due|already (been )?paid|duplicate|already in progress|invalid amount|must be (greater|less) than)\b/i,
    kind: 'validation',
    alertable: false,
  },
  // AVS / CVV / processor rejection — a GATEWAY outcome, never a bank decline.
  {
    re: /\b(avs|address verification|cvv|cvc|cvn|security code|gateway|processor|tokeniz|verification failed)\b/i,
    kind: 'gateway',
    alertable: true,
  },
  // Soft declines — issuer wants a retry / additional auth.
  {
    re: /\b(try again|temporar|insufficient funds|do not honor|reattempt|authentication required|3ds|3-?d ?secure)\b/i,
    kind: 'soft-decline',
    alertable: false,
  },
  // Hard bank declines — business outcome, not alertable.
  {
    re: /\b(declined|card was declined|denied|not authorized)\b/i,
    kind: 'declined',
    alertable: false,
  },
  // Explicit system / SDK-load failure text.
  {
    re: /\b(failed to load|sdk|network|timed out|timeout)\b/i,
    kind: 'system',
    alertable: true,
  },
];

/** Status-driven fallback when no message rule matched. */
function classifyByStatus(
  status: number | undefined,
  message: string,
): PaymentErrorClassification {
  // A 4xx with corrective intent is validation; everything else (5xx, no
  // envelope, raw SDK-load throw) is a system incident so it surfaces in Sentry
  // rather than being silently misreported as a customer decline.
  if (status === 422) {
    return { kind: 'validation', message, alertable: false };
  }
  return { kind: 'system', message, alertable: true };
}

/**
 * Classify a payment error into its taxonomy. Pure — given the same error it
 * always returns the same `{ kind, message, alertable }`.
 */
export function classifyPaymentError(err: unknown): PaymentErrorClassification {
  const envelope: ErrorEnvelopeLike =
    err && typeof err === 'object' ? (err as ErrorEnvelopeLike) : {};
  const message = extractMessage(envelope);
  const status = extractStatus(envelope);
  const haystack = `${message} ${extractCode(envelope)}`;

  const matched = MESSAGE_RULES.find((rule) => rule.re.test(haystack));
  if (matched) {
    return { kind: matched.kind, message, alertable: matched.alertable };
  }

  return classifyByStatus(status, message);
}
